import { FileResponse, FileShortResponse, FileTypeResponse, IFileResponse } from '@api/client';
import { MessageData, QuoteData } from '@chat/interfaces/chat-message-data.interfaces';
import { MESSAGE_TYPE, MessageVm } from '@chat/interfaces/chat.interfaces';
import {
  ChatReplyMessageSnapshot,
  ChatReplyMessageSnapshotData,
} from '@chat/interfaces/chat-reply-snapshot.interfaces';
import { MessageUserInfo } from '@services/web-socket.interfaces';

export function cloneChatQuoteData(quote: QuoteData | null | undefined): QuoteData | null {
  if (!quote?.text) {
    return null;
  }

  const clonedQuote: QuoteData = {
    text: quote.text,
  };

  if (typeof quote.start === 'number') {
    clonedQuote.start = quote.start;
  }
  if (typeof quote.end === 'number') {
    clonedQuote.end = quote.end;
  }

  return clonedQuote;
}

export function clearChatReplyQuoteData(data: MessageData | null | undefined): MessageData | null {
  if (!data) {
    return null;
  }

  if (!data.quote) {
    return data;
  }

  const nextData: MessageData = {
    ...data,
  };
  delete nextData.quote;

  return Object.keys(nextData).length > 0 ? nextData : null;
}

export function cloneChatReplyMessageSnapshot(snapshot: unknown): ChatReplyMessageSnapshot | null {
  if (!isRecord(snapshot)) {
    return null;
  }

  const messageId = normalizeReplyMessageKey(Reflect.get(snapshot, 'messageId'));
  const guid = normalizeReplyMessageKey(Reflect.get(snapshot, 'guid'));
  if (!messageId && !guid) {
    return null;
  }

  const userInfo = Reflect.get(snapshot, 'userInfo');
  const files = Reflect.get(snapshot, 'files');
  const data = Reflect.get(snapshot, 'data');

  return {
    messageId,
    guid,
    fromId: normalizeReplyUserId(Reflect.get(snapshot, 'fromId')),
    message: normalizeReplyMessageText(Reflect.get(snapshot, 'message')),
    userInfo: cloneReplyMessageUserInfo(isMessageUserInfo(userInfo) ? userInfo : null),
    files: cloneReplyFiles(isFileResponseArray(files) ? files : null),
    data: cloneReplyMessageSnapshotData(isMessageData(data) ? data : null),
  };
}

export function createChatReplyMessageSnapshot(message: MessageVm | null | undefined): ChatReplyMessageSnapshot | null {
  if (!message) {
    return null;
  }

  return {
    messageId: normalizeReplyMessageKey(message._id),
    guid: normalizeReplyMessageKey(message.guid),
    fromId: typeof message.from_id === 'number' ? message.from_id : null,
    message: message.message ?? '',
    userInfo: cloneReplyMessageUserInfo(message.user_info),
    files: cloneReplyFiles(message.files),
    data: cloneReplyMessageSnapshotData(message.data),
  };
}

export function hydrateChatReplyMessageSnapshot(snapshot: ChatReplyMessageSnapshot | null | undefined): MessageVm | null {
  const normalizedSnapshot = cloneChatReplyMessageSnapshot(snapshot);
  if (!normalizedSnapshot) {
    return null;
  }

  const messageVm = new MessageVm();
  if (normalizedSnapshot.messageId) {
    messageVm._id = normalizedSnapshot.messageId;
  }
  if (normalizedSnapshot.guid) {
    messageVm.guid = normalizedSnapshot.guid;
  }
  messageVm.chat_id = 0;
  messageVm.created_at = new Date(0).toUTCString();
  messageVm.from_id = typeof normalizedSnapshot.fromId === 'number' ? normalizedSnapshot.fromId : 0;
  messageVm.message = normalizedSnapshot.message;
  messageVm.to_id = null;
  messageVm.type = MESSAGE_TYPE.USER;
  messageVm.files = cloneReplyFiles(normalizedSnapshot.files);
  messageVm.reply_message = null;
  messageVm.readBy = [];
  messageVm.is_delivered = true;
  messageVm.emittedMessage = null;
  messageVm.orderNumber = 0;
  messageVm.data = cloneReplyMessageSnapshotData(normalizedSnapshot.data);
  messageVm.user_info = cloneReplyMessageUserInfo(normalizedSnapshot.userInfo) ?? undefined;
  messageVm.isServiceMessage = false;
  messageVm.pendingUpload = false;
  messageVm.pendingFailed = false;

  return messageVm;
}

function normalizeReplyMessageKey(value: unknown): string | null {
  if (typeof value === 'string') {
    const normalized = value.trim();
    return normalized.length > 0 ? normalized : null;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  return null;
}

function normalizeReplyUserId(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null;
}

function normalizeReplyMessageText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function cloneReplyMessageSnapshotData(
  data: ChatReplyMessageSnapshotData | MessageVm['data'] | null | undefined
): ChatReplyMessageSnapshotData | null {
  if (!data) {
    return null;
  }

  const clonedData: ChatReplyMessageSnapshotData = {};
  if (data.voice === true) {
    clonedData.voice = true;
  }
  if (typeof data.durationMs === 'number') {
    clonedData.durationMs = data.durationMs;
  }
  if (Array.isArray(data.waveform) && data.waveform.length > 0) {
    clonedData.waveform = data.waveform.filter((point: number): boolean => Number.isFinite(point));
  }

  return Object.keys(clonedData).length > 0 ? clonedData : null;
}

function cloneReplyMessageUserInfo(userInfo: MessageUserInfo | null | undefined): MessageUserInfo | null {
  if (!userInfo) {
    return null;
  }

  const clonedUserInfo: MessageUserInfo = {};
  if (typeof userInfo.id === 'number') {
    clonedUserInfo.id = userInfo.id;
  }
  if (typeof userInfo.name === 'string') {
    clonedUserInfo.name = userInfo.name;
  }
  if (typeof userInfo.surname === 'string') {
    clonedUserInfo.surname = userInfo.surname;
  }
  if (typeof userInfo.official === 'boolean') {
    clonedUserInfo.official = userInfo.official;
  }

  const avatar = cloneReplyFile(userInfo.avatar);
  if (avatar) {
    clonedUserInfo.avatar = avatar;
  }

  return Object.keys(clonedUserInfo).length > 0 ? clonedUserInfo : null;
}

function cloneReplyFiles(files: IFileResponse[] | null | undefined): IFileResponse[] {
  if (!Array.isArray(files) || files.length === 0) {
    return [];
  }

  return files
    .map((file: IFileResponse | null | undefined): IFileResponse | null => cloneReplyFile(file))
    .filter((file: IFileResponse | null): file is IFileResponse => !!file);
}

function cloneReplyFile(file: IFileResponse | null | undefined): IFileResponse | null {
  if (!file) {
    return null;
  }

  const clonedFile = new FileResponse();
  if (typeof file.id === 'number') {
    clonedFile.id = file.id;
  }
  if (typeof file.user_id === 'number') {
    clonedFile.user_id = file.user_id;
  }
  if (typeof file.name === 'string') {
    clonedFile.name = file.name;
  }
  if (typeof file.description === 'string') {
    clonedFile.description = file.description;
  }
  if (typeof file.title === 'string') {
    clonedFile.title = file.title;
  }
  if (typeof file.path === 'string') {
    clonedFile.path = file.path;
  }
  if (typeof file.size === 'number') {
    clonedFile.size = file.size;
  }
  if (typeof file.width === 'number') {
    clonedFile.width = file.width;
  }
  if (typeof file.height === 'number') {
    clonedFile.height = file.height;
  }
  if (typeof file.s3name === 'string') {
    clonedFile.s3name = file.s3name;
  }
  if (typeof file.url === 'string') {
    clonedFile.url = file.url;
  }

  const type = cloneReplyFileType(file.type);
  if (type) {
    clonedFile.type = type;
  }

  const origin = cloneReplyFileShort(file.origin);
  if (origin) {
    clonedFile.origin = origin;
  }

  const resizes = cloneReplyFileShortList(file.resizes);
  if (resizes.length > 0) {
    clonedFile.resizes = resizes;
  }

  return clonedFile;
}

function cloneReplyFileType(fileType: FileTypeResponse | null | undefined): FileTypeResponse | null {
  if (!fileType) {
    return null;
  }

  const clonedType = new FileTypeResponse();
  if (typeof fileType.id === 'number') {
    clonedType.id = fileType.id;
  }
  if (typeof fileType.name === 'string') {
    clonedType.name = fileType.name;
  }

  return clonedType.id !== undefined || clonedType.name !== undefined ? clonedType : null;
}

function cloneReplyFileShortList(files: FileShortResponse[] | null | undefined): FileShortResponse[] {
  if (!Array.isArray(files) || files.length === 0) {
    return [];
  }

  return files
    .map((file: FileShortResponse | null | undefined): FileShortResponse | null => cloneReplyFileShort(file))
    .filter((file: FileShortResponse | null): file is FileShortResponse => !!file);
}

function cloneReplyFileShort(file: FileShortResponse | null | undefined): FileShortResponse | null {
  if (!file) {
    return null;
  }

  const clonedFile = new FileShortResponse();
  if (typeof file.id === 'number') {
    clonedFile.id = file.id;
  }
  if (typeof file.url === 'string') {
    clonedFile.url = file.url;
  }
  if (typeof file.width === 'number') {
    clonedFile.width = file.width;
  }
  if (typeof file.height === 'number') {
    clonedFile.height = file.height;
  }

  return clonedFile.id !== undefined
    || clonedFile.url !== undefined
    || clonedFile.width !== undefined
    || clonedFile.height !== undefined
    ? clonedFile
    : null;
}

function isMessageUserInfo(value: unknown): value is MessageUserInfo {
  return isRecord(value);
}

function isFileResponseArray(value: unknown): value is IFileResponse[] {
  return Array.isArray(value) && value.every((file: unknown): boolean => isRecord(file));
}

function isMessageData(value: unknown): value is MessageData {
  return isRecord(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
